import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { trackPageview } from "../lib/analytics";
import { Dices, Gift, Heart, History, Home, RotateCw, User, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PopButton } from "./PopButton";
import { InstallPrompt } from "./InstallPrompt";
import { PickNumberPopup } from "./PickNumberPopup";

const NAV = [
  { to: "/group/new", label: "Groups", icon: Users, id: "groups" },
  { to: "/presents", label: "Presents", icon: Gift, id: "presents" },
  { to: "/wheels", label: "Wheels", icon: RotateCw, id: "wheels" },
  { to: "/history", label: "History", icon: History, id: "history" },
  { to: "/favourites", label: "Saved", icon: Heart, id: "saved" },
];

export function Logo({ className = "text-3xl" }: { className?: string }) {
  return (
    <span className={`ua-logo inline-block ${className}`}>Dither</span>
  );
}

function Header() {
  const { user } = useAuth();
  const [pickOpen, setPickOpen] = useState(false);
  useEffect(() => {
    const open = () => setPickOpen(true);
    window.addEventListener("dither:pick-number", open);
    return () => window.removeEventListener("dither:pick-number", open);
  }, []);
  return (
    <header className="sticky top-0 z-50 border-b-2 border-[#F2D9B8] bg-[#FFF5E1]/90 backdrop-blur-md" data-testid="app-header">
      <div className="mx-auto flex max-w-[1320px] items-center justify-between gap-3 px-3 py-2">
        <Link to="/" data-testid="header-logo-link" aria-label="DITHER home"><Logo className="text-3xl" /></Link>
        <nav className="hidden items-center gap-1 md:flex">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`header-nav-${n.id}`}
              className={({ isActive }) => `rounded-xl px-3 py-2 text-sm font-bold transition-colors ${isActive ? "bg-ink text-white" : "hover:bg-pop-yellow"}`}>
              {n.label}
            </NavLink>
          ))}
          <NavLink to="/account" data-testid="header-nav-account"
            className={({ isActive }) => `ml-1 flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-bold transition-colors ${isActive ? "bg-ink text-white" : "hover:bg-pop-yellow"}`}>
            <User className="h-4 w-4" strokeWidth={2.5} />
            {user ? user.name.split(" ")[0] : "Sign in"}
          </NavLink>
        </nav>
        <PopButton tone="yellow" size="sm" onClick={() => setPickOpen(true)} data-testid="header-im-stuck-button">
          <Dices className="h-4 w-4" strokeWidth={2.5} /> I'm bored
        </PopButton>
        <PickNumberPopup open={pickOpen} onClose={() => setPickOpen(false)} />
      </div>
    </header>
  );
}

const BOTTOM = [{ to: "/", label: "Home", icon: Home, id: "home" }, ...NAV.filter((n) => n.id !== "presents" && n.id !== "wheels"), { to: "/account", label: "Me", icon: User, id: "account" }];

function BottomNav() {
  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 border-t-2 border-[#F2D9B8] bg-[#FFF5E1] md:hidden" data-testid="bottom-nav">
      <div className="grid grid-cols-5">
        {BOTTOM.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`bottom-nav-${n.id}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 text-[11px] font-extrabold uppercase ${isActive ? "text-pop-orange" : "text-ink"}`}>
            <n.icon className="h-5 w-5" strokeWidth={2.5} />
            {n.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

type GoogleFC = { callbackQueue?: unknown[]; showRevocationMessage?: () => void };

function openCookieSettings() {
  const w = window as unknown as { googlefc?: GoogleFC };
  if (w.googlefc?.showRevocationMessage) return w.googlefc.showRevocationMessage();
  toast("Cookie choices are shown to UK & EU visitors. You can also manage ad personalisation at adssettings.google.com.");
}

function SiteFooter() {
  const { user } = useAuth();
  const links = [["/about", "About"], ["/about/paul", "Meet Paul"], ["/contact", "Contact"], ["/privacy", "Privacy"], ...(user && user.role === "admin" ? [["/admin/messages", "Messages"], ["/admin/gifts", "Gifts admin"]] : [])];
  return (
    <footer className="mx-auto mt-4 flex max-w-6xl flex-wrap items-center justify-between gap-2 border-t-[3px] border-[#F2D9B8] px-3 pt-3 text-xs font-extrabold uppercase tracking-wide text-[#6b3a12]" data-testid="site-footer">
      <span>© {new Date().getFullYear()} Stop Dithering</span>
      <nav className="flex flex-wrap gap-3">
        {links.map(([to, label]) => <Link key={to} to={to} className="transition-colors hover:text-[#FF5D1F] hover:underline" data-testid={`footer-link-${label.toLowerCase()}`}>{label}</Link>)}
        <button type="button" onClick={openCookieSettings} className="font-extrabold uppercase tracking-wide transition-colors hover:text-[#FF5D1F] hover:underline" data-testid="footer-cookie-settings">Cookie settings</button>
      </nav>
      <p className="w-full text-[10px] font-semibold normal-case tracking-normal text-[#8a5a2b]" data-testid="footer-affiliate-disclosure">Some gift links are affiliate links: if you buy through them we may earn a small commission, at no extra cost to you.</p>
    </footer>
  );
}

export function Layout() {
  const { pathname } = useLocation();
  useEffect(() => { trackPageview(pathname); }, [pathname]);
  return (
    <>
      <Header />
      <main className="pb-24 md:pb-12">
        <Outlet />
        <SiteFooter />
      </main>
      <BottomNav />
      <InstallPrompt />
    </>
  );
}
